import React from 'react';

/**
 * App-wide error boundary.
 *
 * A render- or effect-time crash in any routed screen is caught here and shown as
 * a recoverable panel, instead of unmounting the whole React tree to a blank white
 * screen (the desktop build ships without an error overlay, so an uncaught throw
 * otherwise leaves the user stuck). The boundary clears itself automatically when
 * the route changes (`resetKey`) and offers an explicit reload.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // Forward to the desktop main-process log when available; never let logging throw.
        try {
            if (typeof window !== 'undefined' && window.electron && window.electron.logError) {
                window.electron.logError({
                    kind: 'react',
                    message: error && error.message,
                    stack: error && error.stack,
                    componentStack: info && info.componentStack,
                    route: window.location ? window.location.pathname : '',
                });
            }
        } catch (e) {
            /* ignore */
        }
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary]', error, info);
    }

    componentDidUpdate(prevProps) {
        // Recover automatically once the user navigates to a different route.
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ maxWidth: 520, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#317787', marginBottom: 8 }}>
                        This screen ran into a problem
                    </div>
                    <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                        Something went wrong while loading this page. Your data is safe — go back and try again.
                    </div>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <button
                            type="button"
                            onClick={() => { window.location.assign('/'); }}
                            style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #317787', background: '#fff', color: '#317787', cursor: 'pointer', fontWeight: 600 }}>
                            Back to Home
                        </button>
                        <button
                            type="button"
                            onClick={() => { window.location.reload(); }}
                            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#f7a440', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                            Reload
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
